import { useEffect, useId, useRef, useState } from 'react'
import { Check, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface SelectOption {
  value: string
  label: string
  description?: string
}

interface SelectProps {
  value?: string
  onChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  label?: string
  error?: string
  className?: string
  'data-testid'?: string
  id?: string
  /** Cuando true, muestra "Cargando…" aunque options esté vacío. */
  isLoading?: boolean
  /** Mensaje a mostrar cuando options=[] y NO está loading. */
  emptyMessage?: string
}

/**
 * Select accesible con soporte role="option" para que tests Playwright puedan
 * consultarlos con getByRole('option', { name: /.../ }).
 */
export function Select({
  value,
  onChange,
  options,
  placeholder = 'Seleccionar…',
  disabled = false,
  label,
  error,
  className,
  'data-testid': testId,
  id,
  isLoading = false,
  emptyMessage,
}: SelectProps) {
  const [open, setOpen] = useState(false)
  // F7.6 ronda 4 — auto drop-up cuando el dropdown no cabe debajo del botón.
  // Bug UX real detectado por tests mobile: cuando el Select está cerca del fondo del
  // viewport (ej. última fila editable de tabla), `top-full` empujaba el listbox fuera
  // del viewport visible y el usuario no podía verlo. Calcular available space al open
  // y flip a `bottom-full` si no cabe.
  const [dropUp, setDropUp] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const autoId = useId()
  const selectId = id ?? autoId
  const listboxId = `${selectId}-listbox`
  const labelId = `${selectId}-label`

  const selected = options.find((o) => o.value === value)

  // Calcular drop-up vs drop-down al abrir (en función del espacio disponible debajo
  // del botón vs el viewport visible). max-height del listbox = 256px (max-h-64).
  useEffect(() => {
    if (!open) {
      setDropUp(false)
      return
    }
    const btn = btnRef.current
    if (!btn) return
    const rect = btn.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const spaceAbove = rect.top
    const listHeight = 256 // max-h-64
    // Si NO cabe abajo pero SÍ cabe arriba → drop-up. Si tampoco cabe arriba, dejar
    // drop-down y que el max-h + overflow-y-auto haga su trabajo.
    if (spaceBelow < listHeight && spaceAbove > spaceBelow) {
      setDropUp(true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (btnRef.current?.contains(t)) return
      if (listRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onClick)
      window.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label && (
        <label id={labelId} htmlFor={selectId} className="text-sm font-medium text-slate-800">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          ref={btnRef}
          id={selectId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-labelledby={label ? labelId : undefined}
          aria-invalid={error ? true : undefined}
          disabled={disabled}
          data-testid={testId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border bg-white px-3 text-sm',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
            error
              ? 'border-[color:var(--color-fitlab-red)] focus-visible:ring-[color:var(--color-fitlab-red)]'
              : 'border-slate-300 focus-visible:ring-[color:var(--color-fitlab-blue)]',
            disabled && 'cursor-not-allowed bg-slate-50 text-slate-400',
          )}
        >
          <span className={cn('truncate text-left', !selected && 'text-slate-400')}>
            {selected ? selected.label : placeholder}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
        </button>
        {open && (
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            data-drop-direction={dropUp ? 'up' : 'down'}
            className={cn(
              'absolute left-0 right-0 z-40 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg',
              dropUp ? 'bottom-full mb-1' : 'top-full mt-1',
            )}
          >
            {options.length === 0 ? (
              isLoading ? (
                <div className="px-3 py-2 text-sm text-slate-400" data-testid="select-loading">
                  Cargando…
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-slate-500" data-testid="select-empty">
                  {emptyMessage ?? 'Sin opciones disponibles'}
                </div>
              )
            ) : (
              options.map((opt) => {
                const isSelected = opt.value === value
                return (
                  <button
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    type="button"
                    onClick={() => {
                      onChange(opt.value)
                      setOpen(false)
                      btnRef.current?.focus()
                    }}
                    className={cn(
                      'flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-slate-100',
                      isSelected && 'bg-slate-50 font-medium text-[color:var(--color-fitlab-blue)]',
                    )}
                  >
                    <Check
                      className={cn(
                        'mt-0.5 h-4 w-4 shrink-0',
                        isSelected ? 'text-[color:var(--color-fitlab-blue)]' : 'invisible',
                      )}
                      aria-hidden
                    />
                    <span className="flex flex-col">
                      <span>{opt.label}</span>
                      {opt.description && (
                        <span className="text-xs text-slate-500">{opt.description}</span>
                      )}
                    </span>
                  </button>
                )
              })
            )}
          </div>
        )}
      </div>
      {error && <p className="text-xs font-medium text-[color:var(--color-fitlab-red)]">{error}</p>}
    </div>
  )
}
